import { atom } from 'jotai';

export type RoleName =
  | 'administrator'
  | 'agent'
  | 'ai'
  | 'light_agent'
  | 'client_restricted'
  | 'sentinel'
  | 'readonly_admin';

export interface PermissionsPayload {
	role: RoleName | null;
	roleId: number | null;
	isSuperAdmin: boolean;
	permissions: string[];
	accountId: number;
	fetchedAt: number;
}

const PERMISSIONS_STORAGE_PREFIX = 'permissions:';
const storageKey = (accountId: number) => `${PERMISSIONS_STORAGE_PREFIX}${accountId}`;

function loadInitial(): PermissionsPayload | null {
	if (typeof window === 'undefined') return null;
	const userJson = localStorage.getItem('currentUser');
	if (!userJson) return null;
	try {
		const user = JSON.parse(userJson) as { accountId?: number } | null;
		if (user?.accountId == null) return null;
		const raw = localStorage.getItem(storageKey(user.accountId));
		if (!raw) return null;
		const parsed = JSON.parse(raw) as PermissionsPayload;
		return parsed.accountId === user.accountId ? parsed : null;
	} catch {
		return null;
	}
}

export const permissionsAtom = atom<PermissionsPayload | null>(loadInitial());

export const setPermissionsAtom = atom(null, (_get, set, payload: PermissionsPayload) => {
	set(permissionsAtom, payload);
	try {
		localStorage.setItem(storageKey(payload.accountId), JSON.stringify(payload));
	} catch {
		// localStorage full or unavailable — atom state still correct
	}
});

export const clearPermissionsAtom = atom(null, (get, set) => {
	const current = get(permissionsAtom);
	if (current) {
		localStorage.removeItem(storageKey(current.accountId));
	}
	set(permissionsAtom, null);
});

export function clearAllPermissionsStorage(): void {
	const keysToRemove: string[] = [];
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i);
		if (key?.startsWith(PERMISSIONS_STORAGE_PREFIX)) {
			keysToRemove.push(key);
		}
	}
	keysToRemove.forEach((k) => localStorage.removeItem(k));
}
